"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  CaretLeft,
  CaretRight,
  Package,
  Warning,
  ArrowsClockwise,
  Cube,
  CurrencyDollar,
  ClockCounterClockwise,
  TrendUp,
  ShoppingCart,
  Storefront,
  Receipt,
  DotsThreeVertical,
  Eye,
  Export,
  FilePdf,
  FileCsv,
  FileXls,
  CheckSquare,
  Square,
  DownloadSimple,
  UploadSimple,
  CaretUp,
  CaretDown,
  ArrowsDownUp,
} from "@phosphor-icons/react";
import { Loader2, AlertTriangle, PackageX } from "lucide-react";
import ProductHistoryDialog from "@/components/custom/products/ProductHistoryDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import {
  getProducts,
  deleteProduct,
  reset,
} from "@/state/products/product.slice";
import { getCategories } from "@/state/category/category.slice";
import { getShops } from "@/state/shops/shop.slice";
import { getBrands } from "@/state/brand/brand.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const API_URL = import.meta.env.VITE_API_URL;

// ============================================
// LOADING SPINNER COMPONENT
// ============================================
const LoadingSpinner = ({ size = "default", text }) => (
  <div className="flex items-center justify-center gap-2">
    <Loader2
      className={`animate-spin ${size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-6 w-6"
        }`}
    />
    {text && <span className="text-sm text-muted-foreground">{text}</span>}
  </div>
);

import { usePermission } from "@/hooks/usePermission";
import { useDebounce } from "@/hooks/useDebounce";

// ============================================
// STOCK BADGE COMPONENT
// ============================================
const StockBadge = ({ quantity, status }) => {
  if (status === "out_of_stock") {
    return (
      <Badge variant="destructive" className="flex items-center gap-1">
        <PackageX className="h-3 w-3" />
        Out of Stock
      </Badge>
    );
  }
  if (status === "low_stock") {
    return (
      <Badge variant="warning" className="flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800">
        <AlertTriangle className="h-3 w-3" />
        Low ({quantity})
      </Badge>
    );
  }
  return (
    <Badge variant="success" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">
      {quantity} in stock
    </Badge>
  );
};

// ============================================
// PRODUCT TABLE ROW COMPONENT
// ============================================
const ProductTableRow = ({ product, onEdit, onDelete, onAdjustStock, onViewHistory, onViewDetails, isDeleting, showCostPrice, canManageProducts, canManageStock }) => {
  const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

  return (
    <TableRow className={isDeleting ? "opacity-50" : ""}>
      <TableCell className="max-w-[250px]">
        <div className="flex items-center gap-3">
          {product.feature_image ? (
            <img
              src={product.feature_image}
              alt={product.title}
              className="w-10 h-10 object-cover rounded flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-100 dark:bg-slate-800 rounded flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span className="font-medium block truncate" title={product.title}>{product.title}</span>
            {product.Category && (
              <p className="text-xs text-muted-foreground truncate">{product.Category.name}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">{formatCurrency(product.current_price)}</TableCell>
      <TableCell className="whitespace-nowrap">{formatCurrency(product.wholesale_price)}</TableCell>
      {showCostPrice && <TableCell className="whitespace-nowrap">{formatCurrency(product.buying_price)}</TableCell>}
      <TableCell className="whitespace-nowrap">
        <StockBadge quantity={product.stockQuantity} status={product.stockStatus} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge variant={product.status === 1 ? "success" : "secondary"}>
          {product.status === 1 ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" disabled={isDeleting}>
              <DotsThreeVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onViewDetails(product)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {canManageProducts && (
              <DropdownMenuItem onClick={() => onEdit(product)}>
                <PencilSimple className="mr-2 h-4 w-4" />
                Edit Product
              </DropdownMenuItem>
            )}
            {canManageStock && (
              <DropdownMenuItem onClick={() => onAdjustStock(product)}>
                <ArrowsClockwise className="mr-2 h-4 w-4 text-blue-500" />
                Adjust Stock
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onViewHistory(product)}>
              <ClockCounterClockwise className="mr-2 h-4 w-4 text-purple-500" />
              View History
            </DropdownMenuItem>
            {canManageProducts && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(product)}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Delete Product
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

// ============================================
// PAGINATION COMPONENT
// ============================================
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rows per page:</span>
        <Select
          value={itemsPerPage.toString()}
          onValueChange={(val) => onItemsPerPageChange(parseInt(val))}
        >
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="500">500</SelectItem>
            <SelectItem value="1000">1000</SelectItem>
            <SelectItem value="5000">5000</SelectItem>
            <SelectItem value="10000">10000</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          {totalItems > 0
            ? `${startItem}-${endItem} of ${totalItems}`
            : "0 items"}
        </span>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <CaretLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <CaretRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// STOCK ADJUSTMENT DIALOG
// ============================================
const StockAdjustmentDialog = ({ product, open, onClose, onSubmit, isLoading }) => {
  const [adjustment, setAdjustment] = useState(0);
  const [reason, setReason] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("add");

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalAdjustment = adjustmentType === "add" ? Math.abs(adjustment) : -Math.abs(adjustment);
    onSubmit(product.id, finalAdjustment, reason);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Stock - {product?.title}</DialogTitle>
          <DialogDescription>
            Current stock: {product?.stockQuantity || 0} units
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Adjustment Type</Label>
            <Select value={adjustmentType} onValueChange={setAdjustmentType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="add">Add Stock</SelectItem>
                <SelectItem value="remove">Remove Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjustment">Quantity</Label>
            <Input
              id="adjustment"
              type="number"
              min="1"
              value={adjustment}
              onChange={(e) => setAdjustment(parseInt(e.target.value) || 0)}
              placeholder="Enter quantity"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. New shipment, Damaged goods"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || adjustment <= 0}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adjusting...
                </>
              ) : (
                `${adjustmentType === "add" ? "Add" : "Remove"} Stock`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// MAIN PRODUCTS PAGE COMPONENT
// ============================================
const ProductsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const showCostPrice = usePermission('ViewCostPrice');
  const canManageProducts = usePermission('Products');
  const canManageStock = usePermission('Stock');

  // Redux state
  const { products, isLoading, success, error, message, pagination, summary } = useSelector(
    (state) => state.product
  );
  const { categories } = useSelector((state) => state.category);
  const { shops } = useSelector((state) => state.shop);
  const { brands } = useSelector((state) => state.brand);
  const { business } = useSelector((state) => state.business);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [stockFilter, setStockFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [shopFilter, setShopFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [adjustingStock, setAdjustingStock] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);

  // Export state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState([
    "title", "sku", "category", "brand", "buying_price", "current_price",
    "wholesale_price", "stockQuantity", "status"
  ]);

  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Available export fields configuration
  const exportFieldsConfig = useMemo(() => {
    const fields = [
      { key: "id", label: "ID" },
      { key: "title", label: "Product Name" },
      { key: "sku", label: "SKU" },
      { key: "category", label: "Category" },
      { key: "brand", label: "Brand" },
      { key: "unit", label: "Unit" },
      { key: "shop", label: "Shop" },
      { key: "buying_price", label: "Buying Price" },
      { key: "current_price", label: "Retail Price" },
      { key: "wholesale_price", label: "Wholesale Price" },
      { key: "stockQuantity", label: "Stock Quantity" },
      { key: "alertQuantity", label: "Alert Quantity" },
      { key: "status", label: "Status" },
      { key: "createdAt", label: "Created Date" },
    ];

    if (!showCostPrice) {
      return fields.filter(f => f.key !== 'buying_price');
    }
    return fields;
  }, [showCostPrice]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Fetch filter options on mount
  useEffect(() => {
    dispatch(getCategories());
    dispatch(getShops());
    dispatch(getBrands());
    dispatch(fetchBusiness());
  }, [dispatch]);

  // Build filter params
  const buildFilterParams = useCallback(() => {
    const params = { 
      page: currentPage, 
      limit: itemsPerPage, 
      search: debouncedSearch,
      sortBy,
      sortOrder,
      stockStatus: stockFilter !== "all" ? stockFilter : undefined
    };
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (brandFilter !== "all") params.brand = brandFilter;
    if (shopFilter !== "all") params.shop = shopFilter;
    return params;
  }, [currentPage, itemsPerPage, debouncedSearch, categoryFilter, brandFilter, shopFilter, sortBy, sortOrder, stockFilter]);

  // Fetch products when any filter/pagination/debounced search changes
  useEffect(() => {
    dispatch(getProducts(buildFilterParams()));
  }, [dispatch, buildFilterParams]);

  // Handle success/error notifications
  useEffect(() => {
    if (success && message) {
      toast({
        title: "✓ Success",
        description: message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      dispatch(reset());
      // Refresh the products list after successful action
      dispatch(getProducts(buildFilterParams()));
    }
    if (error) {
      toast({
        title: "✕ Error",
        description: typeof error === "string" ? error : "An error occurred",
        variant: "destructive",
      });
      dispatch(reset());
    }
    // Only depend on success, error, message - NOT currentPage/itemsPerPage to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error, message]);

  // No client-side filtering needed anymore as it's handled by the backend
  const filteredProducts = useMemo(() => {
    return Array.isArray(products) ? products : [];
  }, [products]);

  // Summary stats - global counts from backend
  const globalStats = useMemo(() => {
    return {
      totalProducts: summary?.totalProducts || pagination?.total || products?.length || 0,
      lowStockCount: summary?.lowStockCount || 0,
      outOfStockCount: summary?.outOfStockCount || 0,
      totalStockValue: summary?.totalStockValue || 0,
    };
  }, [summary, pagination, products]);

  // Financial stats from backend summary (calculated from ALL products)
  const financialStats = useMemo(() => {
    return {
      totalCost: summary?.totalCost || 0,
      totalRetail: summary?.totalRetail || 0,
      totalWholesale: summary?.totalWholesale || 0,
      grossProfit: summary?.grossProfit || 0,
    };
  }, [summary]);

  const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

  // Handlers
  const handleEdit = useCallback((product) => {
    navigate(`/dashboard/products/edit/${product.id}`);
  }, [navigate]);

  const handleViewDetails = useCallback((product) => {
    navigate(`/dashboard/products/${product.id}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((product) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedProduct?.id) {
      setDeletingId(selectedProduct.id);
      setShowDeleteDialog(false);
      toast({
        title: "Deleting product...",
        description: `Removing "${selectedProduct.title}"...`,
      });
      await dispatch(deleteProduct(selectedProduct.id));
    }
  }, [dispatch, selectedProduct, toast]);

  const handleAdjustStock = useCallback((product) => {
    setSelectedProduct(product);
    setShowStockDialog(true);
  }, []);

  const handleStockAdjustment = useCallback(async (productId, adjustment, reason) => {
    setAdjustingStock(true);
    try {
      await axios.patch(
        `${API_URL}products/${productId}/stock`,
        { adjustment, reason },
        { withCredentials: true }
      );
      toast({
        title: "✓ Success",
        description: `Stock ${adjustment > 0 ? "added" : "removed"} successfully`,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      setShowStockDialog(false);
      dispatch(getProducts({ page: currentPage, limit: itemsPerPage }));
    } catch (err) {
      toast({
        title: "✕ Error",
        description: err.response?.data?.message || "Failed to adjust stock",
        variant: "destructive",
      });
    } finally {
      setAdjustingStock(false);
    }
  }, [dispatch, toast, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  }, []);
  
  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }, [sortBy, sortOrder]);

  const SortableTableHead = ({ field, children, className = "" }) => {
    const isActive = sortBy === field;
    return (
      <TableHead 
        className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors select-none ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1 group">
          {children}
          <div className="flex flex-col">
            {isActive ? (
              sortOrder === "asc" ? (
                <CaretUp className="h-3 w-3 text-primary" weight="bold" />
              ) : (
                <CaretDown className="h-3 w-3 text-primary" weight="bold" />
              )
            ) : (
              <ArrowsDownUp className="h-3 w-3 text-slate-300 dark:text-slate-600 group-hover:text-slate-400" />
            )}
          </div>
        </div>
      </TableHead>
    );
  };

  // Toggle export field selection
  const toggleExportField = useCallback((fieldKey) => {
    setSelectedExportFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((f) => f !== fieldKey)
        : [...prev, fieldKey]
    );
  }, []);

  // Select/deselect all export fields
  const toggleAllExportFields = useCallback(() => {
    if (selectedExportFields.length === exportFieldsConfig.length) {
      setSelectedExportFields([]);
    } else {
      setSelectedExportFields(exportFieldsConfig.map((f) => f.key));
    }
  }, [selectedExportFields.length]);

  // Prepare export data
  const prepareExportData = useCallback(() => {
    const data = (filteredProducts || []).map((product) => {
      const row = {};
      selectedExportFields.forEach((fieldKey) => {
        const fieldConfig = exportFieldsConfig.find((f) => f.key === fieldKey);
        if (!fieldConfig) return;

        switch (fieldKey) {
          case "category":
            row[fieldConfig.label] = product.Category?.name || "—";
            break;
          case "brand":
            row[fieldConfig.label] = product.Brand?.name || "—";
            break;
          case "unit":
            row[fieldConfig.label] = product.Unit?.name || "—";
            break;
          case "shop":
            row[fieldConfig.label] = product.shop?.name || "—";
            break;
          case "status":
            row[fieldConfig.label] = product.status === 1 ? "Active" : "Inactive";
            break;
          case "createdAt":
            row[fieldConfig.label] = product.createdAt
              ? new Date(product.createdAt).toLocaleDateString()
              : "—";
            break;
          case "buying_price":
          case "current_price":
          case "wholesale_price":
            row[fieldConfig.label] = parseFloat(product[fieldKey] || 0).toFixed(2);
            break;
          default:
            row[fieldConfig.label] = product[fieldKey] ?? "—";
        }
      });
      return row;
    });
    return data;
  }, [filteredProducts, selectedExportFields]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    const data = prepareExportData();
    if (data.length === 0) {
      toast({ title: "No data", description: "No products to export", variant: "destructive" });
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((h) => `"${String(row[h] || "").replace(/"/g, '""')}"`).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `products_export_${new Date().toISOString().split("T")[0]}.csv`);
  }, [prepareExportData, toast]);

  // Export to Excel
  const exportToExcel = useCallback(() => {
    const data = prepareExportData();
    if (data.length === 0) {
      toast({ title: "No data", description: "No products to export", variant: "destructive" });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, `products_export_${new Date().toISOString().split("T")[0]}.xlsx`);
  }, [prepareExportData, toast]);

  // Export to PDF
  const exportToPDF = useCallback(() => {
    const data = prepareExportData();
    if (data.length === 0) {
      toast({ title: "No data", description: "No products to export", variant: "destructive" });
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const headers = Object.keys(data[0]);

    // Business Details Header
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 15;

    // Business Name
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    const businessName = business?.name || "CetaPOS";
    const titleWidth = doc.getTextWidth(businessName);
    doc.text(businessName, (pageWidth - titleWidth) / 2, yPos);

    // Business Address & Contact
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    yPos += 7;

    const addressLines = [
      business?.address?.street,
      [business?.address?.city, business?.address?.state, business?.address?.zip].filter(Boolean).join(", "),
      business?.address?.country,
      business?.contact?.phone,
      business?.contact?.email
    ].filter(Boolean);

    addressLines.forEach(line => {
      const lineWidth = doc.getTextWidth(line);
      doc.text(line, (pageWidth - lineWidth) / 2, yPos);
      yPos += 5;
    });

    // Divider Line
    yPos += 2;
    doc.setLineWidth(0.5);
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 10;

    // Report Title & Date
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Products Export List", 14, yPos);

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    const dateText = `Generated: ${new Date().toLocaleString()}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, pageWidth - 14 - dateWidth, yPos);

    yPos += 6;
    doc.text(`Total: ${data.length} products`, 14, yPos);
    yPos += 5;

    autoTable(doc, {
      head: [headers],
      body: data.map((row) => headers.map((h) => row[h])),
      startY: yPos,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    doc.save(`products_export_${new Date().toISOString().split("T")[0]}.pdf`);
  }, [prepareExportData, business, toast]);

  // Handle export
  const handleExport = useCallback(async () => {
    if (selectedExportFields.length === 0) {
      toast({ title: "No fields selected", description: "Please select at least one field to export", variant: "destructive" });
      return;
    }

    setExportLoading(true);
    try {
      switch (exportFormat) {
        case "csv":
          exportToCSV();
          break;
        case "excel":
          exportToExcel();
          break;
        case "pdf":
          exportToPDF();
          break;
      }
      toast({
        title: "✓ Export successful",
        description: `Products exported as ${exportFormat.toUpperCase()}`,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      setShowExportDialog(false);
    } catch (error) {
      toast({ title: "Export failed", description: error.message, variant: "destructive" });
    } finally {
      setExportLoading(false);
    }
  }, [selectedExportFields, exportFormat, exportToCSV, exportToExcel, exportToPDF, toast]);

  // Download product template
  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API_URL}products/template/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, 'product_upload_template.xlsx');
      toast({
        title: "✓ Template downloaded",
        description: "Fill in the template and upload to import products",
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error.response?.data?.message || error.message,
        variant: "destructive",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  }, [toast]);

  // Handle file selection for import
  const handleImportFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  }, []);

  // Handle bulk import
  const handleBulkImport = useCallback(async () => {
    if (!importFile) {
      toast({ title: "No file selected", description: "Please select an Excel file to import", variant: "destructive" });
      return;
    }

    setImportLoading(true);
    try {
      // Read the Excel file
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast({ title: "Empty file", description: "The file contains no data to import", variant: "destructive" });
        setImportLoading(false);
        return;
      }

      // Send to backend
      const response = await axios.post(`${API_URL}products/bulk-import`, {
        products: jsonData
      }, { withCredentials: true });

      setImportResults(response.data.data);
      toast({
        title: "✓ Import complete",
        description: response.data.message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });

      // Refresh products list
      dispatch(getProducts(buildFilterParams()));
    } catch (error) {
      toast({
        title: "Import failed",
        description: error.response?.data?.message || error.message,
        variant: "destructive",
      });
    } finally {
      setImportLoading(false);
    }
  }, [importFile, toast, dispatch, buildFilterParams]);

  return (
    <div className="mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">
            Manage your products and inventory
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <DownloadSimple className="mr-2 h-4 w-4" />
            )}
            Template
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowImportDialog(true); setImportFile(null); setImportResults(null); }}
          >
            <UploadSimple className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowExportDialog(true)}
          >
            <Export className="mr-2 h-4 w-4" /> Export
          </Button>
          {canManageProducts && (
            <Button
              onClick={() => navigate("/dashboard/products/add-product")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Global Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 my-2">
        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border shadow-sm">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/30">
            <Cube className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Total Products</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{globalStats.totalProducts}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border shadow-sm">
          <div className="p-2 bg-green-50 dark:bg-green-900/30">
            <CurrencyDollar className="h-5 w-5 text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Stock Value</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-green-600">{formatCurrency(globalStats.totalStockValue)}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-orange-100 dark:border-orange-800 shadow-sm">
          <div className="p-2 bg-orange-50 dark:bg-orange-900/30">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Low Stock</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-orange-500">{globalStats.lowStockCount}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-red-100 dark:border-red-800 shadow-sm">
          <div className="p-2 bg-red-50 dark:bg-red-900/30">
            <PackageX className="h-5 w-5 text-red-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Out of Stock</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-red-500">{globalStats.outOfStockCount}</p>
            )}
          </div>
        </div>
      </div>

      {/* Financial Stats - All Products */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border shadow-sm">
          <div className="p-2 bg-slate-200 dark:bg-slate-700">
            <Receipt className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Total Cost</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-slate-700 dark:text-slate-200">{formatCurrency(financialStats.totalCost)}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-800 shadow-sm">
          <div className="p-2 bg-blue-200 dark:bg-blue-800">
            <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-300" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Retail Value</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formatCurrency(financialStats.totalRetail)}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border border-purple-200 dark:border-purple-800 shadow-sm">
          <div className="p-2 bg-purple-200 dark:bg-purple-800">
            <Storefront className="h-5 w-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Wholesale Value</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{formatCurrency(financialStats.totalWholesale)}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border border-emerald-200 dark:border-emerald-800 shadow-sm">
          <div className="p-2 bg-emerald-200 dark:bg-emerald-800">
            <TrendUp className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Gross Profit</p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className={`text-lg font-bold ${financialStats.grossProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatCurrency(financialStats.grossProfit)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-2 flex-wrap items-center my-4">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            className="pl-9 h-9"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={categoryFilter} onValueChange={(val) => { setCategoryFilter(val); setCurrentPage(1); }}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories?.map((cat) => (
              <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={brandFilter} onValueChange={(val) => { setBrandFilter(val); setCurrentPage(1); }}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Brand" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands?.map((brand) => (
              <SelectItem key={brand.id} value={brand.id.toString()}>{brand.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={shopFilter} onValueChange={(val) => { setShopFilter(val); setCurrentPage(1); }}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Shop" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Shops</SelectItem>
            <SelectItem value="none">No Shop</SelectItem>
            {shops?.map((shop) => (
              <SelectItem key={shop.id || shop._id} value={(shop.id || shop._id).toString()}>{shop.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select 
          value={stockFilter} 
          onValueChange={(val) => { 
            setStockFilter(val); 
            setCurrentPage(1); 
          }}
        >
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Stock" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stock</SelectItem>
            <SelectItem value="in">In Stock</SelectItem>
            <SelectItem value="low">Low Stock</SelectItem>
            <SelectItem value="out">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
        {isLoading && <LoadingSpinner size="sm" />}
      </div>

      {/* Table */}
      <div className="enterprise-table-container">
        {isLoading && !products?.length ? (
          <div className="flex flex-col justify-center items-center h-48 gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading products...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="title">Product</SortableTableHead>
                  <SortableTableHead field="current_price">Retail Price</SortableTableHead>
                  <SortableTableHead field="wholesale_price">Wholesale</SortableTableHead>
                  {showCostPrice && <SortableTableHead field="buying_price">Cost</SortableTableHead>}
                  <SortableTableHead field="stockQuantity">Stock</SortableTableHead>
                  <SortableTableHead field="status">Status</SortableTableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <ProductTableRow
                    key={product.id}
                    product={product}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onAdjustStock={handleAdjustStock}
                    onViewDetails={handleViewDetails}
                    onViewHistory={(p) => {
                      setHistoryProduct(p);
                      setShowHistoryDialog(true);
                    }}
                    isDeleting={deletingId === product.id}
                    showCostPrice={showCostPrice}
                    canManageProducts={canManageProducts}
                    canManageStock={canManageStock}
                  />
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={pagination?.pages || Math.ceil(filteredProducts.length / itemsPerPage)}
              totalItems={pagination?.total || filteredProducts.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold">No products found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || stockFilter !== "all"
                ? "Try a different search or filter"
                : "Add your first product to get started"}
            </p>
          </div>
        )}
      </div>

      {/* Stock Adjustment Dialog */}
      {selectedProduct && (
        <StockAdjustmentDialog
          product={selectedProduct}
          open={showStockDialog}
          onClose={() => setShowStockDialog(false)}
          onSubmit={handleStockAdjustment}
          isLoading={adjustingStock}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedProduct?.title}"? This
              will also remove associated stock records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Export className="h-5 w-5" />
              Export Products
            </DialogTitle>
            <DialogDescription>
              Select format and fields to include in your export.
              {filteredProducts?.length > 0 && (
                <span className="block mt-1 font-medium text-primary">
                  {filteredProducts.length} products will be exported
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Format Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Export Format</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={exportFormat === "csv" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("csv")}
                  className="flex-1"
                >
                  <FileCsv className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button
                  type="button"
                  variant={exportFormat === "excel" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("excel")}
                  className="flex-1"
                >
                  <FileXls className="mr-2 h-4 w-4" />
                  Excel
                </Button>
                <Button
                  type="button"
                  variant={exportFormat === "pdf" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("pdf")}
                  className="flex-1"
                >
                  <FilePdf className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Field Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Select Fields</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllExportFields}
                  className="h-7 text-xs"
                >
                  {selectedExportFields.length === exportFieldsConfig.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-slate-50 dark:bg-slate-900">
                {exportFieldsConfig.map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    onClick={() => toggleExportField(field.key)}
                    className={`flex items-center gap-2 p-2 rounded-md text-sm text-left transition-colors ${selectedExportFields.includes(field.key)
                      ? "bg-primary/10 text-primary border border-primary/30"
                      : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                  >
                    {selectedExportFields.includes(field.key) ? (
                      <CheckSquare className="h-4 w-4 text-primary" weight="fill" />
                    ) : (
                      <Square className="h-4 w-4 text-slate-400" />
                    )}
                    <span className="truncate">{field.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedExportFields.length} of {exportFieldsConfig.length} fields selected
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exportLoading || selectedExportFields.length === 0}
            >
              {exportLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Export className="mr-2 h-4 w-4" />
                  Export {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Products Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Products</DialogTitle>
            <DialogDescription>
              Upload an Excel file to bulk import products. Download the template first for the correct format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Download Template Button */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                Need the template? Download it first to ensure your data is formatted correctly.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
              >
                {downloadingTemplate ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <DownloadSimple className="mr-2 h-4 w-4" />
                )}
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="import-file">Select Excel File</Label>
              <Input
                id="import-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                className="cursor-pointer"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                <h4 className="font-semibold text-sm">Import Results</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Successful: {importResults.success?.length || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">✕</span>
                    <span>Errors: {importResults.errors?.length || 0}</span>
                  </div>
                </div>
                {importResults.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 max-h-24 overflow-y-auto">
                    {importResults.errors.map((err, i) => (
                      <div key={i}>Row {err.row}: {err.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && (
              <Button
                onClick={handleBulkImport}
                disabled={importLoading || !importFile}
              >
                {importLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <UploadSimple className="mr-2 h-4 w-4" />
                    Import Products
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product History Dialog */}
      <ProductHistoryDialog
        productId={historyProduct?.id}
        productName={historyProduct?.title}
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
      />
    </div>
  );
};

export default ProductsPage;
